import { captureLeadTracking } from "@/lib/leadTracking";
import { jsPDF } from "jspdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const ASO_API = `${BACKEND_URL}/api/aso-engine`;

export const buildAsoAuditPayload = (form) => {
  const tracking = captureLeadTracking();
  const storeType = form.app_url.includes("apps.apple.com") ? "app_store" : "google_play";

  return {
    founder_name: form.founder_name,
    work_email: form.work_email,
    company_name: form.company_name,
    app_url: form.app_url,
    store_type: storeType,
    source: "aso_ai_engine",
    page_url: tracking?.page_url || "",
    page_path: tracking?.page_path || "",
    referrer: tracking?.referrer || "",
    session_id: tracking?.session_id || "",
    attribution: tracking?.attribution || undefined,
  };
};

export const buildSampleAsoReport = () => ({
  id: "sample-aso-report",
  founder_name: "Aarav",
  work_email: "sample@equinox.ai",
  company_name: "Northstar Pay",
  app_url: "https://play.google.com/store/apps/details?id=com.sample.app",
  app_name: "Northstar Pay",
  app_identifier: "com.sample.app",
  store_type: "google_play",
  category: "Finance",
  icon_url: "",
  app_rating: 4.2,
  review_count: 58420,
  score_breakdown: {
    rating_health: 84,
    review_volume: 72,
    review_velocity: 69,
    keyword_optimization: 66,
    metadata_optimization: 71,
    screenshots_quality: 63,
    competitor_benchmark: 61,
    sentiment_score: 78,
  },
  dashboard_summary: {
    aso_score: { label: "Overall ASO Score", value: 71, max_value: 100, tone: "blue", insight: "A solid base with clear conversion and keyword upside." },
    rating_health: { label: "Rating Health", value: 84, max_value: 100, tone: "emerald", insight: "Strong trust signal, but still below category leaders." },
    review_velocity: { label: "Review Velocity", value: 69, max_value: 100, tone: "violet", insight: "Review freshness can be improved with better acquisition loops." },
    keyword_coverage: { label: "Keyword Coverage", value: 66, max_value: 100, tone: "blue", insight: "Metadata is missing long-tail discovery opportunities." },
    competitor_gap: { label: "Competitor Gap", value: 39, max_value: 100, tone: "gold", insight: "Top category leaders still own the trust and keyword moat." },
    conversion_potential: { label: "Conversion Potential", value: 73, max_value: 100, tone: "gold", insight: "Better screenshots and social proof could lift store conversion fast." },
    organic_growth_opportunity: { label: "Organic Growth Opportunity", value: 34, max_value: 100, tone: "emerald", insight: "Meaningful upside is available without changing the product itself." },
    sentiment_score: { label: "Sentiment Score", value: 78, max_value: 100, tone: "violet", insight: "Users like the app, but complaint handling speed could be improved." },
  },
  competitor_analysis: [
    { name: "PhonePe", rating: 4.4, review_count: 13700000, keyword_gap: "Payments + rewards", strengths: ["High trust", "Strong review volume"], weaknesses: ["Crowded visuals", "Heavy feature stack"] },
    { name: "Google Pay", rating: 4.3, review_count: 10900000, keyword_gap: "UPI + transfers", strengths: ["Brand trust", "Simple flows"], weaknesses: ["Lower keyword diversity", "Support friction"] },
    { name: "CRED", rating: 4.6, review_count: 3300000, keyword_gap: "Credit score + rewards", strengths: ["Premium positioning", "High conversion creatives"], weaknesses: ["Narrow category fit", "Lower mass-market relevance"] },
    { name: "Paytm", rating: 4.6, review_count: 18400000, keyword_gap: "Wallet + recharge", strengths: ["Category breadth", "Large review base"], weaknesses: ["Complex positioning", "Visual clutter"] },
    { name: "BharatPe", rating: 4.5, review_count: 1450000, keyword_gap: "Merchant payments", strengths: ["Merchant clarity", "Strong trust loops"], weaknesses: ["Niche intent", "Smaller brand search share"] },
  ],
  recommendations: [
    { title: "Improve screenshot conversion rates", body: "Restructure the first three screenshots around trust, feature proof, and the user outcome to increase listing clarity.", priority: "Immediate" },
    { title: "Increase review acquisition", body: "Deploy triggered review prompts after successful transactions and completed onboarding moments.", priority: "High" },
    { title: "Expand long-tail keyword coverage", body: "Broaden the metadata to include high-intent adjacent finance and credit score search terms.", priority: "High" },
    { title: "Optimize title and short description", body: "Tighten the title and short description so they better reflect the product’s strongest use-case keywords.", priority: "Medium" },
    { title: "Respond to negative reviews faster", body: "Set a tighter review-response SLA for low-rating reviews to protect conversion confidence.", priority: "Medium" },
  ],
  video_brief: {
    headline: "Equinox AI Briefing for Northstar Pay",
    script_lines: [
      "Hello Aarav.",
      "We have completed an analysis of your app, Northstar Pay.",
      "Your current ASO Score is 71 out of 100.",
      "Your app currently holds a rating of 4.2 stars with 58,420 reviews.",
      "Compared with industry benchmarks, your keyword visibility is medium.",
      "We identified opportunities in metadata optimization, screenshot optimization, and review acquisition.",
      "Based on our analysis, your app could improve organic growth by approximately 34 percent.",
      "We recommend immediate improvements in keyword coverage, conversion optimization, and app reputation management.",
      "Your app demonstrates medium growth potential.",
      "Click below to schedule your complimentary ASO strategy session with Equinox.",
    ],
    avatar_name: "Equinox AI Strategist",
    status: "mocked",
  },
  progress_stages: [
    { label: "Fetching app metadata", delay_ms: 1500 },
    { label: "Analyzing ratings and reviews", delay_ms: 1500 },
    { label: "Extracting ASO keywords", delay_ms: 1500 },
    { label: "Benchmarking competitors", delay_ms: 1500 },
    { label: "Evaluating screenshots and creatives", delay_ms: 1500 },
    { label: "Measuring sentiment trends", delay_ms: 1500 },
    { label: "Calculating conversion potential", delay_ms: 1500 },
    { label: "Running Equinox AI Engine", delay_ms: 1500 },
    { label: "Generating personalized recommendations", delay_ms: 1500 },
    { label: "Preparing founder video briefing", delay_ms: 1500 },
  ],
  lead_status: "new",
  lead_score: 62,
  sentiment_label: "High",
  growth_potential_percent: 34,
  page_url: "",
  page_path: "/aso-ai-engine",
  referrer: "",
  session_id: "sample",
  attribution: null,
  qualification: { phone_number: "", company_size: "", monthly_installs: "", calendly_booked: false },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
});

export const downloadAsoReportSummary = (report) => {
  const lines = [
    `Equinox ASO AI Engine — ${report.app_name}`,
    `Founder: ${report.founder_name}`,
    `Company: ${report.company_name}`,
    `Store: ${report.store_type}`,
    `ASO Score: ${report.dashboard_summary.aso_score.value}`,
    `Rating: ${report.app_rating}`,
    `Reviews: ${report.review_count}`,
    `Growth Potential: ${report.growth_potential_percent}%`,
    "",
    "Recommendations:",
    ...report.recommendations.map((item, index) => `${index + 1}. ${item.title} — ${item.body}`),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = `${report.app_name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-aso-summary.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

const formatStoreLabel = (storeType) => (storeType === "app_store" ? "App Store" : "Google Play");

const formatLargeNumber = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}k`;
  return `${value}`;
};

const drawWrappedText = (doc, text, x, y, maxWidth, lineHeight = 6, color = [184, 191, 204], fontSize = 11) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);
  const lines = doc.splitTextToSize(text, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeight;
};

const ensurePageSpace = (doc, cursorY, requiredHeight = 24) => {
  if (cursorY + requiredHeight <= 276) return cursorY;
  doc.addPage();
  doc.setFillColor(5, 8, 15);
  doc.rect(0, 0, 210, 297, "F");
  return 20;
};

const drawSectionHeading = (doc, title, subtitle, cursorY) => {
  const nextY = ensurePageSpace(doc, cursorY, 24);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(147, 161, 180);
  doc.text(title.toUpperCase(), 16, nextY);
  doc.setDrawColor(57, 89, 156);
  doc.line(16, nextY + 3, 66, nextY + 3);
  return drawWrappedText(doc, subtitle, 16, nextY + 12, 178, 6, [255, 255, 255], 18);
};

const drawMetricGrid = (doc, metrics, startY) => {
  let cursorY = startY;
  const cardWidth = 42;
  const cardHeight = 28;
  const columns = 4;
  const gap = 4;

  metrics.forEach((metric, index) => {
    if (index % columns === 0) {
      cursorY = ensurePageSpace(doc, cursorY, cardHeight + 8);
    }

    const rowIndex = Math.floor(index / columns);
    const colIndex = index % columns;
    const x = 16 + colIndex * (cardWidth + gap);
    const y = cursorY + rowIndex * (cardHeight + gap);

    doc.setFillColor(12, 18, 30);
    doc.setDrawColor(28, 36, 52);
    doc.roundedRect(x, y, cardWidth, cardHeight, 4, 4, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(130, 144, 166);
    doc.text(metric.label.toUpperCase(), x + 3, y + 7);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text(`${metric.value}`, x + 3, y + 17);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(184, 191, 204);
    doc.text(metric.meta, x + 3, y + 24);
  });

  return cursorY + Math.ceil(metrics.length / columns) * (cardHeight + gap) + 4;
};

export const downloadAsoReportPdf = (report) => {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let cursorY = 0;

  doc.setFillColor(5, 8, 15);
  doc.rect(0, 0, 210, 297, "F");
  doc.setFillColor(16, 22, 35);
  doc.roundedRect(12, 12, 186, 273, 10, 10, "F");
  doc.setDrawColor(34, 47, 70);
  doc.roundedRect(12, 12, 186, 273, 10, 10, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(147, 161, 180);
  doc.text("EQUINOX ASO AI ENGINE", 16, 22);

  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.text(report.app_name || "App Audit", 16, 34);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(184, 191, 204);
  doc.text(`Founder: ${report.founder_name || "—"}`, 16, 43);
  doc.text(`Company: ${report.company_name || "—"}`, 16, 50);
  doc.text(`Store: ${formatStoreLabel(report.store_type)}`, 16, 57);
  doc.text(`Generated: ${new Date(report.updated_at || report.created_at || Date.now()).toLocaleString()}`, 16, 64);

  doc.setFillColor(9, 13, 22);
  doc.roundedRect(128, 20, 56, 44, 8, 8, "F");
  doc.setDrawColor(52, 70, 110);
  doc.roundedRect(128, 20, 56, 44, 8, 8, "S");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(130, 144, 166);
  doc.text("ASO SCORE", 134, 30);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(255, 255, 255);
  doc.text(`${Math.round(report.dashboard_summary.aso_score.value)}`, 134, 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(184, 191, 204);
  doc.text(`${report.growth_potential_percent}% organic upside`, 134, 54);

  cursorY = drawSectionHeading(
    doc,
    "Executive Summary",
    `${report.app_name} currently scores ${Math.round(report.dashboard_summary.aso_score.value)}/100 across the Equinox ASO framework. The listing shows ${report.app_rating?.toFixed?.(1) || report.app_rating} stars from ${report.review_count?.toLocaleString?.() || report.review_count} reviews, with the strongest upside concentrated in ${report.recommendations.slice(0, 3).map((item) => item.title.toLowerCase()).join(", ")}.`,
    82,
  );

  cursorY += 6;
  cursorY = drawMetricGrid(
    doc,
    [
      { label: "ASO Score", value: Math.round(report.dashboard_summary.aso_score.value), meta: "out of 100" },
      { label: "Rating", value: report.app_rating?.toFixed?.(1) || report.app_rating, meta: "store average" },
      { label: "Reviews", value: formatLargeNumber(report.review_count || 0), meta: "public review base" },
      { label: "Lead Score", value: report.lead_score || 0, meta: "internal quality signal" },
      { label: "Sentiment", value: report.sentiment_label || "—", meta: "review quality" },
      { label: "Growth", value: `${report.growth_potential_percent || 0}%`, meta: "organic upside" },
      { label: "Category", value: report.category || "—", meta: "store vertical" },
      { label: "Store", value: formatStoreLabel(report.store_type), meta: "audit source" },
    ],
    cursorY,
  );

  cursorY = drawSectionHeading(doc, "Score Breakdown", "The Equinox scoring model weights eight listing signals to isolate the biggest conversion and discovery gaps.", cursorY + 6);
  Object.entries(report.score_breakdown || {}).forEach(([key, value]) => {
    cursorY = ensurePageSpace(doc, cursorY, 18);
    const label = key.replace(/_/g, " ");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(220, 227, 235);
    doc.text(label.charAt(0).toUpperCase() + label.slice(1), 16, cursorY + 6);
    doc.setFillColor(18, 24, 38);
    doc.roundedRect(72, cursorY + 1, 102, 5, 2, 2, "F");
    doc.setFillColor(37, 99, 235);
    doc.roundedRect(72, cursorY + 1, Math.max(8, value), 5, 2, 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text(`${Math.round(value)}`, 180, cursorY + 6, { align: "right" });
    cursorY += 12;
  });

  cursorY = drawSectionHeading(doc, "Competitor Benchmark", "A quick benchmark view of the category leaders used for the current audit context.", cursorY + 6);
  report.competitor_analysis.forEach((competitor, index) => {
    cursorY = ensurePageSpace(doc, cursorY, 26);
    doc.setFillColor(10, 15, 25);
    doc.setDrawColor(28, 36, 52);
    doc.roundedRect(16, cursorY, 178, 20, 4, 4, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text(`${index + 1}. ${competitor.name}`, 20, cursorY + 7);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(184, 191, 204);
    doc.text(`${competitor.rating.toFixed(1)}★  •  ${competitor.review_count.toLocaleString()} reviews  •  ${competitor.keyword_gap}`, 20, cursorY + 13);
    doc.text(`Strengths: ${competitor.strengths.join(" • ")}`, 20, cursorY + 18);
    cursorY += 24;
  });

  cursorY = drawSectionHeading(doc, "Priority Recommendations", "These recommendations are sequenced for conversion impact, review momentum, and metadata lift.", cursorY + 4);
  report.recommendations.forEach((item, index) => {
    cursorY = ensurePageSpace(doc, cursorY, 26);
    doc.setFillColor(9, 13, 22);
    doc.setDrawColor(28, 36, 52);
    doc.roundedRect(16, cursorY, 178, 22, 4, 4, "FD");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(219, 234, 254);
    doc.text(`${String(index + 1).padStart(2, "0")}  ${item.priority.toUpperCase()}`, 20, cursorY + 7);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.text(item.title, 20, cursorY + 13);
    cursorY = drawWrappedText(doc, item.body, 20, cursorY + 19, 168, 5, [184, 191, 204], 9) + 4;
  });

  cursorY = drawSectionHeading(doc, "Lead Qualification", "Internal consultation details captured after the founder gate.", cursorY + 2);
  const qualification = report.qualification || {};
  const qualificationLines = [
    `Phone Number: ${qualification.phone_number || "Not provided"}`,
    `Company Size: ${qualification.company_size || "Not provided"}`,
    `Monthly Installs: ${qualification.monthly_installs || "Not provided"}`,
    `Calendly booked: ${qualification.calendly_booked ? "Yes" : "No"}`,
  ];
  qualificationLines.forEach((line) => {
    cursorY = ensurePageSpace(doc, cursorY, 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(220, 227, 235);
    doc.text(`• ${line}`, 18, cursorY + 5);
    cursorY += 8;
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(110, 122, 140);
    doc.text(`Equinox Marketing Agency  •  ASO AI Engine Audit`, 16, 289);
    doc.text(`Page ${page} of ${pageCount}`, 194, 289, { align: "right" });
  }

  doc.save(`${(report.app_name || "app").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-full-aso-audit.pdf`);
};